import { Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import { hotTopics, siteInfo } from "@/mocks/site";
import { getCategoryById, getCategoryName, sortByHeat } from "@/utils/articles";

export default function HeroSection() {
  const ranked = sortByHeat(articles);
  const lead = ranked[0];
  const sideList = ranked.slice(1, 4);

  return (
    <section className="border-b border-background-200 bg-background-50">
      <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1.5 text-xs font-medium text-primary-700">
              <span className="flex h-2 w-2 items-center justify-center">
                <span className="h-2 w-2 animate-pulse rounded-full bg-primary-500" />
              </span>
              全网热点 · 实时滚动更新
            </span>

            <h1 className="mt-5 font-heading text-3xl font-black leading-[1.15] tracking-tight text-foreground-950 sm:text-4xl md:text-5xl">
              {siteInfo.brand}
              <span className="mt-2 block text-primary-600">今日猛料 · 热门大瓜一页看全</span>
            </h1>

            <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
              不用来回切换十几个平台。我们把正在发酵的话题、节目动向和讨论焦点按时间线整理好，从起因到最新进展一条链路看完，错过更新也能快速补上。
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to="/materials"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-flashlight-fill" />
                查看今日猛料
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-foreground-400 hover:text-foreground-950"
              >
                了解内容原则
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            <dl className="mt-9 grid grid-cols-2 gap-x-6 gap-y-5 border-t border-background-200 pt-7 sm:grid-cols-4">
              {siteInfo.stats.map((stat) => (
                <div key={stat.label}>
                  <dt className="text-xs text-foreground-500">{stat.label}</dt>
                  <dd className="mt-1 font-heading text-2xl font-bold text-foreground-950">{stat.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="lg:col-span-5">
            <div className="relative flex h-full min-h-[340px] w-full flex-col justify-end overflow-hidden rounded-lg">
              <img
                src={lead.cover}
                alt={`${lead.title} 话题配图`}
                title={`${lead.title} - 看料88热点话题`}
                className="absolute inset-0 h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/92 via-foreground-950/45 to-foreground-950/10" />
              <div className="relative z-10 p-5 md:p-7">
                <span className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-semibold text-foreground-950">
                  <i className="ri-fire-fill" />
                  热度榜首
                </span>
                <h2 className="mt-3 font-heading text-xl font-bold leading-snug text-background-50 md:text-2xl">
                  <Link to={`/material/${lead.slug}`} className="transition-colors hover:text-primary-200">
                    {lead.title}
                  </Link>
                </h2>
                <p className="mt-2 text-xs text-background-300">
                  {getCategoryName(lead.categoryId)} · {lead.heat.toLocaleString()} 人正在围观
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-4 border-t border-background-200 pt-7 lg:flex-row lg:items-start lg:gap-8">
          <div className="flex items-center gap-2 whitespace-nowrap">
            <i className="ri-price-tag-3-line text-accent-600" />
            <span className="font-heading text-sm font-semibold text-foreground-900">正在热议</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {hotTopics.map((topic) => (
              <Link
                key={topic}
                to={`/materials?q=${encodeURIComponent(topic)}`}
                className="whitespace-nowrap rounded-full border border-background-300 bg-background-100 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-100 hover:text-primary-700"
              >
                {topic}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-background-200 bg-background-100">
        <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 divide-background-200 px-4 md:grid-cols-3 md:divide-x md:px-8">
          {sideList.map((item) => (
            <Link
              key={item.id}
              to={`/material/${item.slug}`}
              className="group flex items-start gap-3 border-b border-background-200 py-4 last:border-b-0 md:border-b-0 md:px-5 md:first:pl-0 md:last:pr-0"
            >
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-50 text-secondary-700 transition-colors group-hover:bg-secondary-500 group-hover:text-background-50">
                <i className={`${getCategoryById(item.categoryId)?.icon ?? "ri-article-line"} text-base`} />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                  {item.title}
                </span>
                <span className="mt-1 block text-xs text-foreground-500">
                  {getCategoryName(item.categoryId)} · {item.heat.toLocaleString()} 热度
                </span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}